import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Store, UserPlus, PackagePlus, ShoppingCart, ExternalLink, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { money } from "@/lib/format";

// Self-contained Stripe Connect sample storefront.
// Sections: (1) connected accounts + onboarding, (2) create products, (3) storefront checkout.
export default function ConnectDemo() {
  const qc = useQueryClient();

  // Surface the result of a redirect back from Stripe Checkout.
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("checkout") === "success") toast.success("Payment complete — funds split via destination charge.");
    if (p.get("checkout") === "cancel") toast.info("Checkout canceled.");
    if (p.get("checkout")) window.history.replaceState({}, "", "/connect-demo");
  }, []);

  return (
    <div className="min-h-screen bg-[#F5F1EA] px-4 py-10" data-testid="connect-demo-page">
      <div className="max-w-3xl mx-auto space-y-8">
        <header>
          <h1 className="font-serif text-3xl text-[#1A242B] flex items-center gap-2"><Store className="text-[#144F75]" /> Stripe Connect Sample</h1>
          <p className="text-sm text-[#4A5D6B] mt-1">Onboard sellers, create products, and sell with destination charges (platform keeps an application fee, the rest transfers to the seller).</p>
        </header>
        <AccountsSection qc={qc} />
        <CreateProductSection qc={qc} />
        <Storefront />
      </div>
    </div>
  );
}

function Card({ icon: Icon, title, children }) {
  return (
    <section className="bg-white rounded-2xl border border-[#E5E0D8] p-6">
      <h2 className="font-serif text-xl text-[#1A242B] flex items-center gap-2 mb-4">{Icon && <Icon size={18} className="text-[#144F75]" />} {title}</h2>
      {children}
    </section>
  );
}

// ---------------- 1) Connected accounts + onboarding ----------------
function AccountsSection({ qc }) {
  const [label, setLabel] = useState("");
  const [email, setEmail] = useState("");
  const [fee, setFee] = useState("");
  const [busy, setBusy] = useState(false);
  const { data: accounts = [], isLoading } = useQuery({
    queryKey: ["cs-accounts"],
    queryFn: () => api.get("/connect-sample/accounts").then((r) => r.data),
  });

  const createAccount = async () => {
    if (!label.trim()) return toast.error("Enter a seller label first.");
    setBusy(true);
    try {
      await api.post("/connect-sample/accounts", { label, email, fee_pct: fee === "" ? null : parseFloat(fee) });
      toast.success("Connected account created.");
      setLabel(""); setEmail(""); setFee("");
      qc.invalidateQueries({ queryKey: ["cs-accounts"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const onboard = async (acctId) => {
    try {
      const { data } = await api.post(`/connect-sample/accounts/${acctId}/onboard`, {
        return_url: `${window.location.origin}/connect-demo`,
        refresh_url: `${window.location.origin}/connect-demo`,
      });
      window.location.href = data.url; // Redirect to Stripe-hosted onboarding.
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openDashboard = async (acctId) => {
    try {
      const { data } = await api.post(`/connect-sample/accounts/${acctId}/dashboard`);
      window.open(data.url, "_blank", "noopener");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const editFee = async (a) => {
    const cur = a.fee_pct == null ? "" : String(a.fee_pct);
    const val = window.prompt(`Platform fee % for "${a.label}" (blank = use default 10%):`, cur);
    if (val === null) return;
    try {
      await api.put(`/connect-sample/accounts/${a.account_id}/fee`, { fee_pct: val.trim() === "" ? null : parseFloat(val) });
      toast.success("Seller fee updated.");
      qc.invalidateQueries({ queryKey: ["cs-accounts"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Card icon={UserPlus} title="1 · Connected accounts">
      <div className="flex flex-wrap items-end gap-2 mb-4">
        <label className="text-xs text-[#4A5D6B]">Seller label
          <input value={label} onChange={(e) => setLabel(e.target.value)} data-testid="cs-account-label" placeholder="e.g. Abbi's Studio" className="eyw-input !py-1.5 text-sm mt-1 block w-44" />
        </label>
        <label className="text-xs text-[#4A5D6B]">Email (optional)
          <input value={email} onChange={(e) => setEmail(e.target.value)} data-testid="cs-account-email" placeholder="seller@example.com" className="eyw-input !py-1.5 text-sm mt-1 block w-52" />
        </label>
        <label className="text-xs text-[#4A5D6B]">Fee % (optional)
          <input value={fee} onChange={(e) => setFee(e.target.value)} data-testid="cs-account-fee" type="number" min="0" max="90" step="0.1" placeholder="10" className="eyw-input !py-1.5 text-sm mt-1 block w-24" />
        </label>
        <button onClick={createAccount} disabled={busy} data-testid="cs-create-account" className="eyw-btn !py-2 text-sm disabled:opacity-60">{busy ? <Loader2 className="animate-spin" size={14} /> : "Create account"}</button>
      </div>
      {isLoading ? <Loader2 className="animate-spin text-[#144F75]" /> : accounts.length === 0 ? (
        <p className="text-sm text-[#8FA89B]">No connected accounts yet.</p>
      ) : (
        <ul className="space-y-2">
          {accounts.map((a) => (
            <li key={a.account_id} data-testid={`cs-account-${a.account_id}`} className="flex flex-wrap items-center justify-between gap-2 border-b border-[#E5E0D8] last:border-0 py-2">
              <div>
                <p className="text-sm font-medium text-[#1A242B]">{a.label || a.account_id}</p>
                <p className="text-xs text-[#8FA89B] font-mono">{a.account_id}</p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <button onClick={() => editFee(a)} data-testid={`cs-fee-${a.account_id}`} title="Set platform fee" className="text-xs px-2.5 py-1 rounded-full bg-[#144F75]/10 text-[#144F75] hover:bg-[#144F75]/20">Fee: {a.fee_pct == null ? "default 10%" : `${a.fee_pct}%`}</button>
                {a.ready ? (
                  <>
                    <span className="text-xs px-2.5 py-1 rounded-full bg-[#5C8A72]/15 text-[#5C8A72] flex items-center gap-1"><CheckCircle2 size={12} /> Ready</span>
                    <button onClick={() => openDashboard(a.account_id)} data-testid={`cs-dashboard-${a.account_id}`} className="eyw-btn-ghost !py-1.5 text-xs flex items-center gap-1">Stripe dashboard <ExternalLink size={12} /></button>
                  </>
                ) : (
                  <>
                    <span className="text-xs px-2.5 py-1 rounded-full bg-[#B5794E]/15 text-[#B5794E] flex items-center gap-1"><Clock size={12} /> {a.details_submitted ? "In review" : "Not onboarded"}</span>
                    <button onClick={() => onboard(a.account_id)} data-testid={`cs-onboard-${a.account_id}`} className="eyw-btn-ghost !py-1.5 text-xs flex items-center gap-1">Onboard to collect payments <ExternalLink size={12} /></button>
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// ---------------- 2) Create products ----------------
function CreateProductSection({ qc }) {
  const [f, setF] = useState({ name: "", description: "", price: "", connected_account_id: "", fee_pct: "" });
  const [busy, setBusy] = useState(false);
  const { data: accounts = [] } = useQuery({ queryKey: ["cs-accounts"], queryFn: () => api.get("/connect-sample/accounts").then((r) => r.data) });
  const readyAccounts = accounts.filter((a) => a.account_id);

  const createProduct = async () => {
    if (!f.name.trim() || !f.price || !f.connected_account_id) return toast.error("Name, price and seller are required.");
    setBusy(true);
    try {
      await api.post("/connect-sample/products", {
        name: f.name, description: f.description,
        price_in_cents: Math.round(parseFloat(f.price) * 100),
        currency: "usd", connected_account_id: f.connected_account_id,
        fee_pct: f.fee_pct === "" ? null : parseFloat(f.fee_pct),
      });
      toast.success("Product created.");
      setF({ name: "", description: "", price: "", connected_account_id: "", fee_pct: "" });
      qc.invalidateQueries({ queryKey: ["cs-products"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Card icon={PackagePlus} title="2 · Create a product">
      <div className="grid sm:grid-cols-2 gap-3">
        <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="cs-product-name" placeholder="Product name" className="eyw-input !py-2 text-sm" />
        <input value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} data-testid="cs-product-price" type="number" min="0.50" step="0.01" placeholder="Price (USD)" className="eyw-input !py-2 text-sm" />
        <input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="cs-product-desc" placeholder="Description (optional)" className="eyw-input !py-2 text-sm sm:col-span-2" />
        <select value={f.connected_account_id} onChange={(e) => setF({ ...f, connected_account_id: e.target.value })} data-testid="cs-product-account" className="eyw-input !py-2 text-sm">
          <option value="">Select seller (connected account)</option>
          {readyAccounts.map((a) => <option key={a.account_id} value={a.account_id}>{a.label || a.account_id}{a.ready ? "" : " (not onboarded)"}</option>)}
        </select>
        <input value={f.fee_pct} onChange={(e) => setF({ ...f, fee_pct: e.target.value })} data-testid="cs-product-fee" type="number" min="0" max="90" step="0.1" placeholder="Fee % (optional — overrides seller)" className="eyw-input !py-2 text-sm" />
        <button onClick={createProduct} disabled={busy} data-testid="cs-create-product" className="eyw-btn !py-2 text-sm disabled:opacity-60 sm:col-span-2">{busy ? <Loader2 className="animate-spin" size={14} /> : "Create product"}</button>
      </div>
    </Card>
  );
}

// ---------------- 3) Storefront ----------------
function Storefront() {
  const { data: products = [], isLoading } = useQuery({ queryKey: ["cs-products"], queryFn: () => api.get("/connect-sample/products").then((r) => r.data) });
  const { data: accounts = [] } = useQuery({ queryKey: ["cs-accounts"], queryFn: () => api.get("/connect-sample/accounts").then((r) => r.data) });
  const labelFor = (id) => accounts.find((a) => a.account_id === id)?.label || id;

  const buy = async (product_id) => {
    try {
      const { data } = await api.post("/connect-sample/checkout", { product_id, quantity: 1, origin_url: window.location.origin });
      window.location.href = data.checkout_url; // Redirect to Stripe hosted checkout.
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Card icon={ShoppingCart} title="3 · Storefront">
      {isLoading ? <Loader2 className="animate-spin text-[#144F75]" /> : products.length === 0 ? (
        <p className="text-sm text-[#8FA89B]">No products yet — create one above.</p>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {products.map((p) => (
            <div key={p.product_id} data-testid={`cs-product-${p.product_id}`} className="border border-[#E5E0D8] rounded-xl p-4 flex flex-col">
              <p className="text-sm font-semibold text-[#1A242B]">{p.name}</p>
              {p.description && <p className="text-xs text-[#4A5D6B] mt-0.5">{p.description}</p>}
              <p className="text-xs text-[#8FA89B] mt-1">Seller: {labelFor(p.connected_account_id)}{p.fee_pct != null ? ` · fee ${p.fee_pct}%` : ""}</p>
              <div className="flex items-center justify-between mt-3">
                <span className="font-serif text-lg text-[#144F75]">{money(((p.amount || 0) / 100))}</span>
                <button onClick={() => buy(p.product_id)} data-testid={`cs-buy-${p.product_id}`} className="eyw-btn !py-1.5 text-sm">Buy</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
